import {BaseElementAsync, MsgLabel, OSkinableInit} from "back/commons/basis";
import {AppFrameDeskFeat, IApp, IAppCtx} from "back/core/appFrame";
import {IReg, IRegPointer, IUiEnv, REG} from 'lib/commons/registry';
import {IView, VIEWS} from "lib/commons/views";
import {DOM, JSX} from "lib/commons/xml/dom";
import {IChainEnv} from "lib/wsp/chain";
import {AppHeader} from "back/core/widgets/appHeader";
import {DOMSH} from "lib/commons/xml/domsh";
import {JTaskAppDef} from "back/wsp/plugins/tasksPlg";
import {ItemViewerSingle} from "back/wsp/views/itemViewerSingle";
import {IWspEnv, IWspUiEnv, Wsp} from "lib/wsp/wsp";
import {ERROR} from "lib/core/errorReport";
import {registerWspProtocolsActions} from "back/wsp/actions/scProtocolActions";
import {WSPPACK} from "lib/wsp/pack";
import "back/wsp/apps/apps_Perms";
import "back/wsp/plugins/plugins_Perms";
import {Action} from "lib/commons/actions";
import {JWspAppDef} from "back/wsp/plugins/wspsPlg";
import {InfoReqCurrentItem} from "lib/wsp/item";
import {srcRef, srcUri} from "lib/wsp/src";
import {IInfo, IInfoBroker, IInfoConsumer, InfoBrokerBasic} from "lib/commons/infos";
import {InfoReqTaskContentItems, InfoTaskCurrentItemsChange} from "back/wsp/views/task/taskContent";

/** App affichant une tâche. */
export interface TaskApp extends BaseElementAsync {
	initialize(init: IAppCtx<IChainEnv & IUiEnv> & OSkinableInit): this;
}

export class TaskApp extends BaseElementAsync implements IView, IApp<IWspUiEnv>, IInfoConsumer {

	appErrorStatus?: 'accessDenied' | 'wspNotAvailable'

	appDef: JTaskAppDef;

	/** A ce niveau, on a spécifié le reg pour le wsp, cf taskViewer pour la spécif au niveau item/task */
	reg: IReg<IWspUiEnv>;

	msgElt: MsgLabel;

	rootElt: HTMLDivElement;

	appHeader: AppHeader<TaskApp>;

	wsp: Wsp;

	infoBroker: IInfoBroker;

	_currentMainContentItem?: srcRef;

	protected _taskViewer: ItemViewerSingle | null;

	protected _wspLabel: HTMLElement | null;

	/** mémoire des derniers labels utilisés pour le label de la view. */
	protected _lastSrcLabel: string;

	protected _lastWspLabel: string;

	protected async _initialize(init: IAppCtx<IChainEnv & IUiEnv>) {
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);
		this.infoBroker = new InfoBrokerBasic();
		this.infoBroker.addConsumer(this);

		this.msgElt = sr.appendChild(new MsgLabel());

		try {
			const fromReg = this.findReg(init) as IReg<IChainEnv & IUiEnv>;
			this.appDef = init.appDef as JTaskAppDef;
			const place = fromReg.env.universe.wspServer.wspsLive.newPlace();
			this.wsp = place.getWsp(this.appDef.w);

			if (!this.wsp.wspServer.chain.auth.currentUser) {
				this.msgElt.setStandardMsg('needAuth');
				return;
			}

			try {
				await this.wsp.waitForAvailable(this);
			} catch (e) {
				console.log(e);
				this.appErrorStatus = 'wspNotAvailable';
				this.msgElt.setCustomMsg("Échec à l'affichage de cette tâche : l'atelier n'est pas accessible.", "error");
				return;
			}
			if (this.wsp.isDeleted) {
				this.msgElt.setCustomMsg("Échec à l'affichage de cette tâche : l'atelier n'est pas accessible.", "error");
				this.appErrorStatus = 'wspNotAvailable';
				return;
			}

			this.reg = REG.createSubReg(fromReg, this.wsp);
			this.reg.env.place = place;
			this.reg.env.place.eventsMgr.on("wspLiveStateChange", this.onWspLiveStateChange.bind(this));
			this.reg.env.uiRoot = this;
			this.reg.env.infoBroker = this.infoBroker;

			await this.initRegExtPoints();

			//check perm pour cette ihm.
			if (!this.reg.hasPerm("ui.taskApp")) {
				this.msgElt.setStandardMsg('accessDenied');
				this.appErrorStatus = 'accessDenied';
				return;
			}

			this.rootElt = sr.appendChild(<div id="root"/>) as HTMLDivElement;
			this._taskViewer = new ItemViewerSingle({disableAutoChangeCurrentItem: true});
			await this._taskViewer.initViewer(this.reg, this.rootElt, init.lastDatas, this.appDef.task);

			//AppHeader (après les autres éléments pour déclarer les focusListening)
			this.appHeader = new AppHeader<TaskApp>().initialize({
				reg: this.reg,
				mainActions: this.reg.getList("actions:taskApp:bar:main"),
				moreActions: this.reg.getList("actions:taskApp:bar:more"),
				actionContext: this,
				focusListening: this.rootElt
			});
			sr.insertBefore(this.appHeader, this.rootElt);
			const h = this.appHeader.appendChild(<header/>);
			const img = h.appendChild(<img src={this.wsp.wspMetaUi.getIcon() || "/@skin@/wsp/objects/wsp/wsp.svg"}/>);
			img.title = WSPPACK.buildWspDefTitle(this.wsp.infoWsp.wspType);
			this._wspLabel = h.appendChild(<h1/>);
			this._refreshTitle();

			try {
				const lstns = this.reg.getList<(app: IApp<IWspEnv>) => void | Promise<void>>("taskApp:load");
				if (lstns) for (const lstn of lstns) {
					const result = lstn(this);
					if (result instanceof Promise) await result;
				}
			} catch (e) {
				await ERROR.log("taskApp:load listener failed", e);
			}

		} catch (e) {
			this.setMsg("Échec à l'affichage de cette tâche.", "error", true);
		}
	}

	protected _refreshTitle(srcLabel?: string) {
		const wspLabel = this.wsp?.wspTitle || "[Atelier non disponible]";
		if (this._wspLabel) DOM.setTextContent(this._wspLabel, wspLabel);
		DOM.setAttr(this, "label", srcLabel ? srcLabel + " - " + wspLabel : wspLabel);
		if (this._lastSrcLabel !== srcLabel || this._lastWspLabel !== wspLabel)
			VIEWS.dispatchViewChange(this);
		this._lastSrcLabel = srcLabel;
		this._lastWspLabel = wspLabel;
	}

	/** Init des extPoints du registre de l'app. */
	protected async initRegExtPoints() {
		const app = this;
		const reg = this.reg;
		const wsp = reg.env.wsp;

		//#### ScProtocol
		registerWspProtocolsActions(reg);

		//#### MainBar actions
		function addMainBarAction(action: Action<IRegPointer<IWspUiEnv>>, accel?: string) {
			reg.addToList("actions:taskApp:bar:main", action.getId(), 1, action);
			if (accel)
				reg.addToList("accelkeys:taskApp:global", accel, 1, action);
		}

		addMainBarAction(new OpenWspApp(), "F8");
		//addMainBarAction(new OpenWspDocApp(), ); TODO peu de chance que l'item à ouvrir soit un doc

		//#### MainBar actions
		function addMoreBarAction(action: Action<IRegPointer<IWspUiEnv>>, accel?: string) {
			reg.addToList("actions:taskApp:bar:more", action.getId(), 1, action);
			if (accel)
				reg.addToList("accelkeys:taskApp:global", accel, 1, action);
		}

		addMoreBarAction(new OpenContentItemOnWspApp());

	}

	setMsg(msg: string, level?: 'info' | 'error' | 'warning' | null, isFatal: boolean = false) {
		this.msgElt.setCustomMsg(msg, level);
		if (this.rootElt) DOM.setHidden(this.rootElt, isFatal);
	}

	protected async onWspLiveStateChange(wsp: Wsp) {
		if (wsp !== this.wsp) return;
		this._refreshTitle(null);
		if (this.appHeader) await this.appHeader.refresh();
		if (!wsp.isAvailable || wsp.isDeleted) {
			this.appErrorStatus = 'wspNotAvailable';
			this.setMsg("Échec à l'affichage de cette tâche : l'atelier n'est pas accessible.", "error", true);
		}
	}

	onViewShown(): void {
		if (this._taskViewer) this._taskViewer.onViewShown();
	}

	onViewHidden(closed?: boolean) {
		try {
			if (this._taskViewer) this._taskViewer.onViewHidden(closed);
		} finally {
			if (closed) this.reg.env.place.closePlace();
		}
	}

	updateAppDef(def: JTaskAppDef): boolean {
		return false;
	}

	onInfo(info: IInfo): void {
		if (this.initialized) {
			if (info instanceof InfoTaskCurrentItemsChange) {
				this.appHeader.refresh();
			}
		} else {
			this.initializedAsync.then(() => this.onInfo(info));
		}
	}
}

REG.reg.registerSkin('wsp-task-app', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
	}

	c-msg {
	  flex: unset;
	}

	#root {
		display: contents;
	}

	wsp-taskmain {
		background-color: var(--bgcolor);
	}

	c-appheader {
		min-height: 2rem;
		z-index: 1;
		margin: 0 4px;
	}

	header {
		flex: 1;
		display: flex;
		min-width: 0;
		min-height: 0;
		max-height: 5em;
		overflow: hidden;
		align-items: center;
		justify-content: center;
		user-select: none;
	}

	img {
		height: 1.3em;
	}

	h1 {
		margin: .1em .5em;
		font-size: 1em;
		letter-spacing: 0.1em;
		text-align: center;
	}

	/*
  hr {
	  display: none;
  }

  main {
	  flex: 1;
	  display: flex;
	  min-width: 0;
	  min-height: 0;
	  flex-direction: column;
	  border-inline-start: 3px solid transparent;
	  border-inline-end: 3px solid transparent;
  }
	*/
`);

customElements.define('wsp-task-app', TaskApp);

class OpenWspApp extends Action<TaskApp> {
	constructor() {
		super('openWsp');
		this._label = "Afficher la tâche dans l'atelier en mode explorateur";
		this._icon = "/@skin@/wsp/views/spaceTree/tree.svg";
		this.requireVisiblePerm("ui.wspApp");
	}

	execute(ctx: TaskApp, ev?: Event): any | "noPreventDefault" {
		const curr = new InfoReqCurrentItem();
		ctx.infoBroker.dispatchInfo(curr, ctx);
		(desk as AppFrameDeskFeat).findAndOpenApp({u: ctx.reg.env.universe.getId(), wsp: ctx.wsp.code, srcRef: ctx.appDef.task} as JWspAppDef, ev);
	}
}

/**
 * Ouverture (si existant) du contentItem courant dans l'app wspApp
 */
class OpenContentItemOnWspApp extends Action<TaskApp> {
	constructor() {
		super('openWsp');
		this._label = "Ouvrir l'item dans l'atelier en mode explorateur";
		this._icon = "/@skin@/wsp/apps/taskApp/ctItemTree.svg";
		this.setVisible((ctx) => this.findCurentContentItems(ctx) ? true : false);
	}

	isVisible(ctx: TaskApp): boolean {
		if (!this.checkObjectRootVisiblePerm(ctx, ["ui.wspApp", "ui.app.task.open.ctItem.inWspApp"], true)) return false;
		return super.isVisible(ctx);
	}

	execute(ctx: TaskApp, ev?: Event): any | "noPreventDefault" {
		const curr = new InfoReqCurrentItem();
		ctx.infoBroker.dispatchInfo(curr, ctx);
		(desk as AppFrameDeskFeat).findAndOpenApp({u: ctx.reg.env.universe.getId(), wsp: ctx.wsp.code, srcRef: this.findCurentContentItems(ctx)} as JWspAppDef, ev);
	}

	/** Sélection de l'item content à afficher
	 * FIXME : regle de priorité par rapport à la signature ? Tous les proposer si plusieurs dans un menu bouton ? */
	private findCurentContentItems(ctx: TaskApp): srcUri | null {
		const curr = new InfoReqTaskContentItems();
		ctx.infoBroker.dispatchInfo(curr, ctx);
		if (curr.entries)
			return curr.entries[0].srcUri || curr.entries[0].shortDesc.srcUri;
		return null;
	}
}